/**
 * Hyreskontrakt för garage / garageplats (Formulär R-13) — IR twin of the
 * legacy `garage@v1` React template.
 *
 * Legal text verbatim from `base-templates/Hyreskontrakt för garage – kopiera.docx`
 * (same content as `gdpr/R-14_Hyreskontrakt_garage_garageplats.docx`,
 * Haymanot-approved). DO NOT paraphrase or reorder: a wording change is a
 * new block version.
 */
import { checkbox, group, nodes, option, optionList, p, section, t, val } from '@hyranai/regta-document-ir';
import { annexRefs, dateVal, fieldRows, numVal, optionChosen, partyDetailsBlock, propertySlot, referencedAttachments, selectSlot, shown, signingBlockDefinition, textVal, ticked, whenOption, } from '../common/index.js';
import { clauseBody } from './kit.js';
const SOURCE = 'base-templates/Hyreskontrakt för garage – kopiera.docx';
const LEGAL = {
    plane: 'legal',
    status: 'published',
    language: 'sv-SE',
    legalReview: { reviewedBy: 'Haymanot', source: SOURCE },
};
/* ------------------------------------------------------------------ */
/* Option codebooks (ADR-0062) — value = legacy stored value            */
/* ------------------------------------------------------------------ */
export const GARAGE_FORM_OPTIONS = [
    { value: 'Helt garage', label: 'Helt garage', code: 'whole_garage', summary: 'The lease covers an entire garage.' },
    { value: 'Garageplats', label: 'Garageplats (i större anläggning)', code: 'garage_space', summary: 'The lease covers a garage space within a larger facility.' },
];
export const GARAGE_TEMPERATURE_OPTIONS = [
    { value: 'Kallgarage', label: 'Kallgarage', code: 'cold_garage', summary: 'Unheated (cold) garage.' },
    { value: 'Varmgarage', label: 'Varmgarage', code: 'heated_garage', summary: 'Heated (warm) garage.' },
];
export const SHARED_USE_OPTIONS = [
    { value: 'Alla dagar och tider', label: 'Alla dagar och tider', code: 'all_days_times', summary: 'May be used on all days and at all times.' },
    { value: 'Nedanstående dagar och tider', label: 'Nedanstående dagar och tider', code: 'specified_days_times', summary: 'May be used only on the days and times specified below.' },
    { value: 'Enligt bilaga', label: 'Enligt bilaga', code: 'per_annex', summary: 'Usage times are specified in an annex.' },
];
export const ELECTRICITY_OPTIONS = [
    { value: 'Utan elanslutning', label: 'Utan elanslutning', code: 'no_connection', summary: 'No electrical connection.' },
    {
        value: 'Med elanslutning för motorvärmare/kupévärmare',
        label: 'Med elanslutning för motorvärmare/kupévärmare',
        code: 'engine_heater',
        summary: 'Electrical connection for an engine or cabin heater.',
    },
    {
        value: 'Med elanslutning för annan användning',
        label: 'Med elanslutning för annan användning',
        code: 'other_use',
        summary: 'Electrical connection for some other use, with a stated max wattage.',
    },
    { value: 'Med laddare', label: 'Med laddare', code: 'ev_charger', summary: 'Electrical connection with an EV charger.' },
];
export const RENT_INCLUSION_OPTIONS = [
    { value: 'Total hyra', label: 'Total hyra', code: 'total_rent', summary: 'Rent is all-inclusive.' },
    { value: 'Hyra exkl. tillägg', label: 'Hyra exkl. tillägg', code: 'rent_excl_surcharges', summary: 'Rent is stated excluding separate surcharges.' },
];
export const RENT_ADJUSTMENT_OPTIONS = [
    { value: 'Indexklausul enligt bilaga', label: 'Indexklausul enligt bilaga', code: 'index_clause', summary: 'Rent is index-adjusted per a separate index clause annex.' },
    { value: 'Fast årlig höjning', label: 'Fast årlig höjning per 1 januari', code: 'fixed_annual_increase', summary: 'Rent rises by a fixed percentage each year on 1 January.' },
];
export const ELECTRICITY_COST_OPTIONS = [
    { value: 'Ingår i hyran', label: 'Ingår i hyran', code: 'included_in_rent', summary: 'Electricity cost is included in the rent.' },
    { value: 'Enligt mätare', label: 'Enligt mätare', code: 'by_meter', summary: 'Electricity is billed according to a meter.' },
    { value: 'Andel av gemensam förbrukning', label: 'Andel av gemensam förbrukning', code: 'shared_consumption', summary: 'Electricity is billed as a share of shared consumption.' },
    { value: 'Annat sätt', label: 'Annat sätt', code: 'other_method', summary: 'Electricity cost is calculated some other described way.' },
    { value: 'Ej relevant', label: 'Ej relevant', code: 'not_applicable', summary: 'Electricity cost is not applicable.' },
];
export const VAT_OPTIONS = [
    { value: 'Upplåtelsen är mervärdesskattepliktig', label: 'Upplåtelsen är mervärdesskattepliktig', code: 'vat_liable', summary: 'The lease is subject to VAT.' },
    { value: 'Upplåtelsen är ej mervärdesskattepliktig', label: 'Upplåtelsen är ej mervärdesskattepliktig', code: 'vat_exempt', summary: 'The lease is not subject to VAT.' },
];
export const PAYMENT_PERIOD_OPTIONS = [
    { value: 'Kalenderkvartals början', label: 'Kalenderkvartals början', code: 'quarterly', summary: 'Rent is paid at the start of each calendar quarter.' },
    { value: 'Kalendermånads början', label: 'Kalendermånads början', code: 'monthly', summary: 'Rent is paid at the start of each calendar month.' },
];
export const PAYMENT_TYPE_OPTIONS = [
    { value: 'Enligt avisering i faktura', label: 'Enligt avisering i faktura (rekommenderas)', code: 'invoice', summary: 'Paid per the invoice/notice sent to the tenant.' },
    { value: 'Plusgiro', label: 'Plusgiro', code: 'plusgiro', summary: 'Paid to a plusgiro account number.' },
    { value: 'Bankgiro', label: 'Bankgiro', code: 'bankgiro', summary: 'Paid to a bankgiro account number.' },
];
const [FORM_WHOLE, FORM_SPACE] = GARAGE_FORM_OPTIONS;
const [TEMP_COLD, TEMP_WARM] = GARAGE_TEMPERATURE_OPTIONS;
const [USE_ALL, USE_SPECIFIED, USE_ANNEX] = SHARED_USE_OPTIONS;
const [EL_NONE, EL_HEATER, EL_OTHER, EL_CHARGER] = ELECTRICITY_OPTIONS;
const [ADJ_INDEX, ADJ_FIXED] = RENT_ADJUSTMENT_OPTIONS;
const [COST_INCLUDED, COST_METER, COST_SHARED, COST_OTHER] = ELECTRICITY_COST_OPTIONS;
const [PAY_INVOICE, PAY_PG, PAY_BG] = PAYMENT_TYPE_OPTIONS;
const [PERIOD_QUARTER, PERIOD_MONTH] = PAYMENT_PERIOD_OPTIONS;
/** Option item ticked when the answer denotes `opt` (code or legacy value). */
const box = (ctx, options, value, opt, content, children) => option(ticked(ctx, optionChosen(options, value, opt)), content, { code: opt.code, ...(children ? { children } : {}) });
/** The legacy prints these two boxes inside the sentence; the IR has no inline option node. */
const inlineBox = (checked, sourceKey) => checkbox(checked, sourceKey);
const hasElectricConnection = (value) => [EL_HEATER, EL_OTHER, EL_CHARGER].some((o) => optionChosen(ELECTRICITY_OPTIONS, value, o));
/** Legacy `attachmentLabel()`: "Bilaga N" once resolved, else the placeholder lead and a fill-in rule. */
const annexNumber = (ctx, value, lead, key) => annexRefs(ctx, value) ?? [t(lead), val(null, { blankWidth: 'short', sourceKey: key })];
/* ------------------------------------------------------------------ */
/* § Hyresvärd / § Hyresgäst                                           */
/* ------------------------------------------------------------------ */
export const hyresvard = partyDetailsBlock({
    blockKey: 'garage.hyresvard',
    ref: 'hyresvard',
    title: 'Hyresvärd',
    summary: 'The landlord: name, id number, address and phone, each pre-filled from the lessor party.',
    role: 'lessor',
    ordinal: 0,
    fields: [
        { key: 'landlord_name', label: 'Namn', field: 'name', required: true },
        { key: 'landlord_org_number', label: 'Personnr / Org.nr', field: 'legalId', required: true },
        { key: 'landlord_address', label: 'Adress', field: 'address', required: true },
        { key: 'landlord_phone', label: 'Telefon', field: 'phone' },
    ],
});
export const hyresgast = partyDetailsBlock({
    blockKey: 'garage.hyresgast',
    ref: 'hyresgast',
    title: 'Hyresgäst',
    summary: 'The tenant: name, id number, billing (avisering) address and phone, each pre-filled from the lessee party.',
    role: 'lessee',
    ordinal: 0,
    fields: [
        { key: 'tenant_name', label: 'Namn', field: 'name', required: true },
        { key: 'tenant_personnummer', label: 'Personnr / Org.nr', field: 'legalId', required: true, help: 'PII — ej sökbart' },
        { key: 'tenant_billing_address', label: 'Aviseringsadress', field: 'address', required: true },
        { key: 'tenant_phone', label: 'Telefon', field: 'phone' },
    ],
});
/* ------------------------------------------------------------------ */
/* § Garage / Garageplats                                              */
/* ------------------------------------------------------------------ */
export const objekt = {
    ...LEGAL,
    blockKey: 'garage.objekt',
    version: 'v1',
    title: 'Garage / Garageplats',
    summary: 'Where the garage is (municipality, property, address), whether it is a whole garage or a numbered space, cold or heated, and its area.',
    slots: [
        propertySlot('property_municipality', 'Kommun', 'municipality', { required: true }),
        propertySlot('property_designation', 'Fastighetsbeteckning', 'designation', { required: true }),
        propertySlot('garage_street', 'Adress för fastigheten', 'address', { required: true }),
        selectSlot('garage_form', 'Typ', GARAGE_FORM_OPTIONS, { required: true, default: FORM_SPACE.code }),
        { key: 'garage_place_number', label: 'Garageplats nr', type: 'text', visibleWhen: whenOption('garage_form', FORM_SPACE) },
        selectSlot('garage_temperature', 'Temperatur', GARAGE_TEMPERATURE_OPTIONS, { required: true, default: TEMP_COLD.code }),
        { key: 'area_sqm', label: 'Area (ca m²)', type: 'number', min: 0 },
    ],
    compile: (input, ctx) => [
        section('garage', 'Garage / Garageplats', nodes(fieldRows([
            ['Kommun', [textVal(input, 'property_municipality')]],
            ['Fastighetsbeteckning', [textVal(input, 'property_designation')]],
            ['Adress för fastigheten', [textVal(input, 'garage_street')]],
        ], { columns: 2 }), p('Avser:'), group([
            group([
                optionList([
                    box(ctx, GARAGE_FORM_OPTIONS, input.garage_form, FORM_WHOLE, 'Helt garage'),
                    box(ctx, GARAGE_FORM_OPTIONS, input.garage_form, FORM_SPACE, [t('Garageplats nr '), textVal(input, 'garage_place_number', 'short')]),
                ], { sourceKey: 'garage_form', select: 'single', layout: 'inline' }),
                optionList([box(ctx, GARAGE_TEMPERATURE_OPTIONS, input.garage_temperature, TEMP_COLD, 'Kallgarage'), box(ctx, GARAGE_TEMPERATURE_OPTIONS, input.garage_temperature, TEMP_WARM, 'Varmgarage')], { sourceKey: 'garage_temperature', select: 'single', layout: 'inline' }),
            ]),
            group(nodes(fieldRows([['Area (ca m²)', [numVal(ctx, input, 'area_sqm')]]]))),
        ], { variant: 'columns' }))),
    ],
};
/* ------------------------------------------------------------------ */
/* § Användning                                                        */
/* ------------------------------------------------------------------ */
export const anvandning = {
    ...LEGAL,
    blockKey: 'garage.anvandning',
    version: 'v1',
    title: 'Användning',
    summary: 'The garage is let as-is for parking the stated number of cars, of which some are rechargeable.',
    slots: [
        { key: 'car_count', label: 'Antal personbilar', type: 'number', required: true, min: 0, integer: true, default: 1 },
        { key: 'ev_count', label: 'Varav laddbara el-/hybridbilar', type: 'number', min: 0, integer: true, default: 0 },
    ],
    compile: (input, ctx) => [
        section('anvandning', 'Användning', nodes(p('Garaget / garageplatsen upplåts, om inget annat anges, i befintligt skick för att användas till uppställningsplats för följande av hyresgästen nyttjade fordon:'), fieldRows([
            ['Antal personbilar', [numVal(ctx, input, 'car_count'), t(' st.')]],
            ['Varav laddbara el-/hybridbilar', [numVal(ctx, input, 'ev_count'), t(' st.')]],
        ]))),
    ],
};
/* ------------------------------------------------------------------ */
/* § Samnyttjande                                                      */
/* ------------------------------------------------------------------ */
export const samnyttjande = {
    ...LEGAL,
    blockKey: 'garage.samnyttjande',
    version: 'v1',
    title: 'Samnyttjande',
    summary: 'When the garage may be used: always, on stated days and times, or per an annex.',
    slots: [
        selectSlot('shared_use', 'Bilplatsen får nyttjas', SHARED_USE_OPTIONS, { required: true, default: USE_ALL.code }),
        {
            key: 'shared_use_annex',
            label: 'Bilaga – nyttjandetider',
            type: 'attachment',
            required: true,
            visibleWhen: whenOption('shared_use', USE_ANNEX),
            help: 'Välj eller skapa en bilaga som anger nyttjandetiderna.',
        },
        { key: 'shared_days', label: 'Veckodagar', type: 'text', visibleWhen: whenOption('shared_use', USE_SPECIFIED), help: 'T.ex. "måndag–fredag"' },
        { key: 'shared_time_from', label: 'Från kl', type: 'text', visibleWhen: whenOption('shared_use', USE_SPECIFIED), help: 'T.ex. "08:00"' },
        { key: 'shared_time_to', label: 'Till kl', type: 'text', visibleWhen: whenOption('shared_use', USE_SPECIFIED) },
    ],
    compile: (input, ctx) => {
        const annexes = referencedAttachments(ctx, input.shared_use_annex);
        return {
            nodes: [
                section('samnyttjande', 'Samnyttjande', nodes(optionList([
                    box(ctx, SHARED_USE_OPTIONS, input.shared_use, USE_ALL, 'Alla dagar och tider'),
                    box(ctx, SHARED_USE_OPTIONS, input.shared_use, USE_SPECIFIED, 'Nedanstående dagar och tider'),
                    box(ctx, SHARED_USE_OPTIONS, input.shared_use, USE_ANNEX, 'Enligt bilaga'),
                ], { sourceKey: 'shared_use', select: 'single', layout: 'inline' }), shown(ctx, optionChosen(SHARED_USE_OPTIONS, input.shared_use, USE_SPECIFIED)) &&
                    p([
                        t('Veckodagarna '),
                        textVal(input, 'shared_days'),
                        t(' mellan kl '),
                        textVal(input, 'shared_time_from', 'short'),
                        t(' och kl '),
                        textVal(input, 'shared_time_to', 'short'),
                        t('.'),
                    ]), shown(ctx, optionChosen(SHARED_USE_OPTIONS, input.shared_use, USE_ANNEX)) &&
                    p([t('Nyttjandetiderna framgår av '), ...annexNumber(ctx, input.shared_use_annex, 'bilaga ', 'shared_use_annex'), t('.')]))),
            ],
            attachments: optionChosen(SHARED_USE_OPTIONS, input.shared_use, USE_ANNEX) ? annexes : [],
        };
    },
};
/* ------------------------------------------------------------------ */
/* § Elström                                                           */
/* ------------------------------------------------------------------ */
export const elstrom = {
    ...LEGAL,
    blockKey: 'garage.elstrom',
    version: 'v1',
    title: 'Elström',
    summary: 'Whether the garage has an electrical connection, and for what: engine heater, other use (max watt) or a charger.',
    slots: [
        selectSlot('electricity', 'Elanslutning', ELECTRICITY_OPTIONS, { required: true, default: EL_NONE.code }),
        { key: 'electricity_max_watt', label: 'Max effekt (watt)', type: 'number', min: 0, integer: true, visibleWhen: whenOption('electricity', EL_OTHER) },
        { key: 'charger_type', label: 'Anslutningstyp (laddare)', type: 'text', visibleWhen: whenOption('electricity', EL_CHARGER), help: 'T.ex. Type 2' },
    ],
    compile: (input, ctx) => [
        section('elstrom', 'Elström', [
            optionList([
                box(ctx, ELECTRICITY_OPTIONS, input.electricity, EL_NONE, 'Utan elanslutning'),
                box(ctx, ELECTRICITY_OPTIONS, input.electricity, EL_HEATER, 'Med elanslutning för motorvärmare/kupévärmare'),
                box(ctx, ELECTRICITY_OPTIONS, input.electricity, EL_OTHER, [
                    t('Med elanslutning för annan användning, max '),
                    numVal(ctx, input, 'electricity_max_watt'),
                    t(' watt'),
                ]),
                box(ctx, ELECTRICITY_OPTIONS, input.electricity, EL_CHARGER, [t('Med laddare, anslutningstyp '), textVal(input, 'charger_type')]),
            ], { sourceKey: 'electricity', select: 'single' }),
        ]),
    ],
};
/* ------------------------------------------------------------------ */
/* § Hyrestid / § Uppsägning och förlängning                           */
/* ------------------------------------------------------------------ */
export const hyrestid = {
    ...LEGAL,
    blockKey: 'garage.hyrestid',
    version: 'v1',
    title: 'Hyrestid',
    summary: 'The lease term: start and end date.',
    slots: [
        { key: 'start_date', label: 'Fr.o.m.', type: 'date', required: true },
        { key: 'end_date', label: 'T.o.m.', type: 'date', required: true },
    ],
    compile: (input, ctx) => [
        section('hyrestid', 'Hyrestid', nodes(fieldRows([['Fr.o.m.', [dateVal(ctx, input, 'start_date')]], ['T.o.m.', [dateVal(ctx, input, 'end_date')]]], { columns: 2 }))),
    ],
};
export const uppsagning = {
    ...LEGAL,
    blockKey: 'garage.uppsagning',
    version: 'v1',
    title: 'Uppsägning och förlängning',
    summary: 'Written notice period before the term ends; otherwise the lease renews for the stated extension.',
    slots: [
        { key: 'notice_months', label: 'Uppsägningstid (mån)', type: 'number', required: true, min: 0, integer: true, default: 3 },
        { key: 'extension_months', label: 'Förlängningstid (mån)', type: 'number', required: true, min: 0, integer: true, default: 12 },
    ],
    compile: (input, ctx) => [
        section('uppsagning', 'Uppsägning och förlängning', [
            p([
                t('Uppsägning av detta kontrakt ska ske skriftligen minst '),
                numVal(ctx, input, 'notice_months'),
                t(' månad(er) före den avtalade hyrestidens utgång. I annat fall är kontraktet för varje gång förlängt med '),
                numVal(ctx, input, 'extension_months'),
                t(' månad(er).'),
            ]),
        ]),
    ],
};
/* ------------------------------------------------------------------ */
/* § Hyra / § Ändring av hyra                                          */
/* ------------------------------------------------------------------ */
export const hyra = {
    ...LEGAL,
    blockKey: 'garage.hyra',
    version: 'v1',
    title: 'Hyra',
    summary: 'Annual rent in kronor, and whether it is all-inclusive or excludes surcharges.',
    slots: [
        { key: 'annual_rent', label: 'Hyra (kr/år)', type: 'currency', required: true, min: 0 },
        selectSlot('rent_inclusion', 'Hyresstruktur', RENT_INCLUSION_OPTIONS, { required: true, default: RENT_INCLUSION_OPTIONS[0].code }),
    ],
    compile: (input, ctx) => [
        section('hyra', 'Hyra', [
            p([t('Kronor '), numVal(ctx, input, 'annual_rent'), t(' per år.')]),
            optionList(RENT_INCLUSION_OPTIONS.map((o) => box(ctx, RENT_INCLUSION_OPTIONS, input.rent_inclusion, o, o.value)), { sourceKey: 'rent_inclusion', select: 'single', layout: 'inline' }),
        ]),
    ],
};
export const andringHyra = {
    ...LEGAL,
    blockKey: 'garage.andring-hyra',
    version: 'v1',
    title: 'Ändring av hyra',
    summary: 'How the rent changes: per an index-clause annex, or a fixed percentage every 1 January from a given year.',
    slots: [
        selectSlot('rent_adjustment', 'Justeringsmodell', RENT_ADJUSTMENT_OPTIONS, { required: true, default: ADJ_FIXED.code }),
        {
            key: 'rent_index_annex',
            label: 'Bilaga – indexregleringsklausul',
            type: 'attachment',
            required: true,
            visibleWhen: whenOption('rent_adjustment', ADJ_INDEX),
            help: 'Välj eller skapa en indexregleringsbilaga (mall: Indexavtal).',
        },
        {
            key: 'fixed_increase_start_year',
            label: 'Första höjning fr.o.m. (år)',
            type: 'text',
            visibleWhen: whenOption('rent_adjustment', ADJ_FIXED),
            help: 'T.ex. "2027"',
        },
        { key: 'fixed_increase_percent', label: 'Höjning per år (%)', type: 'number', min: 0, max: 100, visibleWhen: whenOption('rent_adjustment', ADJ_FIXED) },
    ],
    compile: (input, ctx) => {
        const isIndex = optionChosen(RENT_ADJUSTMENT_OPTIONS, input.rent_adjustment, ADJ_INDEX);
        const annexes = referencedAttachments(ctx, input.rent_index_annex);
        return {
            nodes: [
                section('andring-hyra', 'Ändring av hyra', [
                    optionList([
                        box(ctx, RENT_ADJUSTMENT_OPTIONS, input.rent_adjustment, ADJ_INDEX, 'Indexklausul enligt bilaga', shown(ctx, isIndex)
                            ? [
                                p([
                                    t('Indexreglering av hyran sker enligt särskild bilaga. '),
                                    ...annexNumber(ctx, input.rent_index_annex, 'Bilaga ', 'rent_index_annex'),
                                    t('.'),
                                ]),
                            ]
                            : undefined),
                        box(ctx, RENT_ADJUSTMENT_OPTIONS, input.rent_adjustment, ADJ_FIXED, [
                            t('Per 1 januari varje år fr.o.m. '),
                            textVal(input, 'fixed_increase_start_year', 'short'),
                            t(' med '),
                            numVal(ctx, input, 'fixed_increase_percent'),
                            t(' procent.'),
                        ]),
                    ], { sourceKey: 'rent_adjustment', select: 'single' }),
                ]),
            ],
            attachments: isIndex ? annexes : [],
        };
    },
};
/* ------------------------------------------------------------------ */
/* § Kostnad för elström / § Mervärdesskatt / § Hyrans betalning       */
/* ------------------------------------------------------------------ */
export const elkostnad = {
    ...LEGAL,
    blockKey: 'garage.elkostnad',
    version: 'v1',
    title: 'Kostnad för elström',
    summary: 'How electricity is paid: included, by meter, as a share of shared consumption, or another described way.',
    slots: [
        selectSlot('electricity_cost', 'Kostnad för elström', ELECTRICITY_COST_OPTIONS, { required: true, default: COST_INCLUDED.code }),
        {
            key: 'electricity_cost_description',
            label: 'Beräkningsgrund (annat sätt)',
            type: 'textarea',
            visibleWhen: whenOption('electricity_cost', COST_OTHER),
        },
    ],
    compile: (input, ctx) => [
        section('elkostnad', 'Kostnad för elström', nodes(
        // "Ej relevant" is an editor option only — the form prints four boxes.
        optionList([COST_INCLUDED, COST_METER, COST_SHARED, COST_OTHER].map((o) => box(ctx, ELECTRICITY_COST_OPTIONS, input.electricity_cost, o, o.value)), { sourceKey: 'electricity_cost', select: 'single', layout: 'inline' }), shown(ctx, optionChosen(ELECTRICITY_COST_OPTIONS, input.electricity_cost, COST_OTHER)) && p([textVal(input, 'electricity_cost_description', 'full')]))),
    ],
};
export const moms = {
    ...LEGAL,
    blockKey: 'garage.moms',
    version: 'v1',
    title: 'Mervärdesskatt (moms)',
    summary: 'Whether the lease is VAT-liable; if so the tenant pays VAT on top of the rent.',
    slots: [selectSlot('vat_status', 'Mervärdesskatt', VAT_OPTIONS, { required: true, default: VAT_OPTIONS[1].code })],
    compile: (input, ctx) => [
        section('moms', 'Mervärdesskatt (moms)', [
            optionList(VAT_OPTIONS.map((o) => box(ctx, VAT_OPTIONS, input.vat_status, o, o.value)), { sourceKey: 'vat_status', select: 'single' }),
            p('Om upplåtelsen är mervärdesskattepliktig ska hyresgästen utöver hyran betala vid varje tillfälle gällande moms.'),
        ]),
    ],
};
export const betalning = {
    ...LEGAL,
    blockKey: 'garage.betalning',
    version: 'v1',
    title: 'Hyrans betalning',
    summary: 'Rent is paid in advance before each calendar quarter or month, per invoice or into the landlord’s account.',
    slots: [
        selectSlot('payment_period', 'Betalningsperiod', PAYMENT_PERIOD_OPTIONS, { required: true, default: PERIOD_QUARTER.code }),
        selectSlot('payment_type', 'Betalningssätt', PAYMENT_TYPE_OPTIONS, { required: true, default: PAY_INVOICE.code }),
        {
            key: 'payment_account',
            label: 'Hyresvärdens konto (BG/PG + nummer)',
            type: 'text',
            help: 'T.ex. BG 123-4567',
            visibleWhen: whenOption('payment_type', PAY_PG, PAY_BG),
        },
    ],
    compile: (input, ctx) => [
        section('betalning', 'Hyrans betalning', [
            p([
                t('Hyran tillsammans med förekommande tillägg ska betalas i förskott senast sista vardagen före '),
                inlineBox(ticked(ctx, optionChosen(PAYMENT_PERIOD_OPTIONS, input.payment_period, PERIOD_QUARTER)), 'payment_period'),
                t(' kalenderkvartals början '),
                inlineBox(ticked(ctx, optionChosen(PAYMENT_PERIOD_OPTIONS, input.payment_period, PERIOD_MONTH)), 'payment_period'),
                t(' kalendermånads början.'),
            ]),
            optionList([
                box(ctx, PAYMENT_TYPE_OPTIONS, input.payment_type, PAY_INVOICE, 'Hyran betalas enligt avisering.'),
                option(ticked(ctx, optionChosen(PAYMENT_TYPE_OPTIONS, input.payment_type, PAY_PG) || optionChosen(PAYMENT_TYPE_OPTIONS, input.payment_type, PAY_BG)), [t('Hyran betalas genom insättning på Hyresvärdens konto: '), textVal(input, 'payment_account')]),
            ], { sourceKey: 'payment_type', select: 'single' }),
        ]),
    ],
};
/* ------------------------------------------------------------------ */
/* § Hantering av personuppgifter / § Särskilda bestämmelser           */
/* ------------------------------------------------------------------ */
export const personuppgifter = {
    ...LEGAL,
    blockKey: 'garage.personuppgifter',
    version: 'v1',
    title: 'Hantering av personuppgifter',
    summary: 'Ticks that the tenant’s privacy notice is enclosed as an annex.',
    slots: [
        {
            key: 'privacy_info_annex',
            label: 'Information till hyresgäst om behandling av personuppgifter, se bilaga',
            type: 'boolean',
            default: true,
        },
    ],
    compile: (input, ctx) => [
        section('personuppgifter', 'Hantering av personuppgifter', [
            optionList([
                option(ticked(ctx, input.privacy_info_annex === true || input.privacy_info_annex === 'true'), 'Information till hyresgäst om behandling av personuppgifter, se bilaga.'),
            ], { select: 'multi', sourceKey: 'privacy_info_annex' }),
        ]),
    ],
};
export const sarskildaBestammelser = {
    ...LEGAL,
    blockKey: 'garage.sarskilda-bestammelser',
    version: 'v1',
    title: 'Särskilda bestämmelser',
    summary: 'Free-text special terms agreed for this lease.',
    slots: [{ key: 'special_conditions', label: 'Särskilda bestämmelser', type: 'textarea', help: 'Ange eventuella särskilda villkor här.' }],
    compile: (input) => [
        section('sarskilda-bestammelser', 'Särskilda bestämmelser', [p([textVal(input, 'special_conditions', 'full')])]),
    ],
};
/* ------------------------------------------------------------------ */
/* Static, legally-reviewed clauses (unnumbered, as in the source)     */
/* ------------------------------------------------------------------ */
const AVTALSVILLKOR_TEXT = 'Hyresgästen får inte utan hyresvärdens skriftliga medgivande överlåta hyresrätten eller sätta annan i sitt ställe.\n\n' +
    'a) Hyresgästen får inte överlåta hyresrätten eller hyra ut garaget/garageplatsen i andra hand utan hyresvärdens medgivande.\n\n' +
    'b) Hyresgästen förbinder sig\n' +
    '• att i garaget inte införa andra typer av fordon än som anges i detta kontrakt,\n' +
    '• att i garaget inte utföra andra än smärre reparations- och justeringsarbeten såsom byte av tändstift, däck o.d,\n' +
    '• att i garaget inte utföra biltvätt om inte hyresvärden tillhandahåller särskild för ändamålet iordningsställd spolplatta,\n' +
    '• att teckna och vidmakthålla erforderlig brand- och stöldförsäkring för nämnda fordon, att följa gällande brandförsäkringsvillkor och garageföreskrifter,\n' +
    '• att ansvara för all skada inom garaget som vållas av hyresgästen själv, hyresgästens hushållsmedlemmar eller av hyresgästen anlitade personer\n\n' +
    'c) Hyresvärden är inte ersättningsskyldig för genom brand, åverkan eller annan anledning eventuellt uppkommande skada på eller stöld av hyresgästens fordon med tillbehör och reservdelar samt däri förvarade effekter, såvida inte skadan vållats av hyresvärden eller dennes personal.\n\n' +
    'd) För upplåtelse av garage/garageplats med eluttag eller laddare gäller särskilt\n' +
    '• att för ändamålet godkänd adapter eller anslutningskabel utöver eventuell fast kabel tillhandahålls av hyresgästen,\n' +
    '• att endast elapparater, adapter och anslutningskabel som uppfyller gällande säkerhetskrav får användas,\n' +
    '• att eluttaget alltid ska hållas låst,\n' +
    '• att anslutningskabel inte får vara ansluten till eluttaget då kabeln inte är kopplad till elapparat,\n' +
    '• att hyresgästen omedelbart ska anmäla uppkommet fel på eluttaget eller laddaren till hyresvärden eller dennes ombud,\n' +
    '• att hyresgästen svarar för att elapparat, anslutet fordon, anslutningskabel och adapter vid användningen är i fullgott skick,\n' +
    '• att endast det eluttag eller laddpunkt som tillhör garageplatsen får användas samt\n' +
    '• att säkerhetsföreskrifter och bruksanvisning som framgår av anslag vid laddare eller som tillställts hyresgästen på annat sätt måste iakttas vid användning av laddaren.';
const EL_SARSKILT_TEXT = 'För upplåtelse av garage / garageplats med eluttag eller laddare gäller särskilt:\n\n' +
    '• att för ändamålet godkänd adapter och anslutningskabel utöver fast kabel tillhandahålls av hyresgästen,\n' +
    '• att endast elapparat, adapter och anslutningskabel som uppfyller gällande säkerhetskrav får användas,\n' +
    '• att eluttaget inte får användas för laddning av el-/hybridfordon om det inte särskilt godkänts av hyresvärden,\n' +
    '• att eluttaget alltid ska hållas låst,\n' +
    '• att anslutningskabel inte får vara ansluten till eluttaget då kabeln inte är kopplad till elapparat,\n' +
    '• att hyresgästen omedelbart till hyresvärden ska anmäla uppkomna fel på eluttaget eller laddaren,\n' +
    '• att hyresgästen ansvarar för att elapparat, anslutet fordon, anslutningskabel och adapter vid användningen är i fullgott skick,\n' +
    '• att endast det eluttag eller laddpunkt som tillhör garaget / garageplatsen får användas,\n' +
    '• att säkerhetsföreskrifter och bruksanvisning som framgår av anslag vid laddaren eller som tillställts hyresgästen på annat sätt iakttas vid användning av laddaren.';
const OVERLATELSE_TEXT = 'Hyresrätten till garaget / garageplatsen enligt detta avtal får inte utan hyresvärdens medgivande överlåtas eller inskrivas.\n\n' +
    'Hyresgästens rätt enligt detta avtal är förverkad och hyresvärden berättigad att säga upp avtalet till omedelbart upphörande om hyresgästen inte i rätt tid betalar angiven hyra tillsammans med förekommande tillägg och moms eller i övrigt bryter mot bestämmelserna i 8 kap. 23 § jordabalken.\n\n' +
    'För nyttjanderätten av garaget / garageplatsen gäller särskilda ordningsföreskrifter.';
const unnumberedClause = (ref, title, body) => section(ref, title, clauseBody(body), { numbering: 'none', variant: 'clause', keepTogether: true });
export const avtalsvillkor = {
    ...LEGAL,
    blockKey: 'garage.avtalsvillkor',
    version: 'v1',
    title: 'Avtalsvillkor',
    summary: 'Standard terms: no assignment or subletting, tenant undertakings, landlord liability carve-out, and rules for electrical outlets/chargers.',
    slots: [],
    compile: () => [unnumberedClause('avtalsvillkor', 'Avtalsvillkor', AVTALSVILLKOR_TEXT)],
};
export const elSarskilt = {
    ...LEGAL,
    blockKey: 'garage.el-sarskilt',
    version: 'v1',
    title: 'Särskilt för garage med eluttag eller laddare',
    summary: 'Extra safety terms that apply only when the garage has an electrical connection or charger.',
    slots: [selectSlot('electricity', 'Elanslutning', ELECTRICITY_OPTIONS)],
    compile: (input, ctx) => shown(ctx, hasElectricConnection(input.electricity))
        ? [unnumberedClause('el-sarskilt', 'Särskilt för garage med eluttag eller laddare', EL_SARSKILT_TEXT)]
        : null,
};
export const overlatelse = {
    ...LEGAL,
    blockKey: 'garage.overlatelse',
    version: 'v1',
    title: 'Överlåtelse, förverkande och ordningsföreskrifter',
    summary: 'No assignment without consent; forfeiture on late payment or breach under 8 kap. 23 § jordabalken; house rules apply.',
    slots: [],
    compile: () => [unnumberedClause('overlatelse', 'Överlåtelse, förverkande och ordningsföreskrifter', OVERLATELSE_TEXT)],
};
/* ------------------------------------------------------------------ */
/* § Underskrifter                                                     */
/* ------------------------------------------------------------------ */
export const underskrift = signingBlockDefinition({
    blockKey: 'garage.underskrift',
    summary: 'Signature panels for both parties, with the digital or physical-originals statement above them.',
});
export const GARAGE_BLOCKS = [
    hyresvard,
    hyresgast,
    objekt,
    anvandning,
    samnyttjande,
    elstrom,
    hyrestid,
    uppsagning,
    hyra,
    andringHyra,
    elkostnad,
    moms,
    betalning,
    personuppgifter,
    sarskildaBestammelser,
    avtalsvillkor,
    elSarskilt,
    overlatelse,
    underskrift,
];
//# sourceMappingURL=blocks.js.map